import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { ThreePillars } from '../components/ThreePillars';
import { HowItWorks } from '../components/HowItWorks';
import { MemeWall } from '../components/MemeWall';
import { TrustBlock } from '../components/TrustBlock';
import { PetversePreview } from '../components/PetversePreview';
import { JoinSection } from '../components/JoinSection';
import { Footer } from '../components/Footer';
import { RainbowRoomPopup } from '../components/RainbowRoomPopup';
import { getApiUrl } from '../lib/utils';
import { getSavedSource } from '../lib/sourceTracking';

const LandingPage = () => {
  // Explanation sections (Three Pillars, How It Works, Guardian Feed, Trust
  // Block, Petverse Preview) behave as a single accordion: only one can be
  // open at a time. They all start collapsed, showing just their title/arrow.
  const [openSection, setOpenSection] = React.useState(null);
  const toggleSection = (id) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  // Ensure direct links with a URL fragment (e.g. petechtor.org/#waitlist)
  // reliably scroll to that section, even on a cold/hard page load where
  // the browser resolves the hash before React has rendered the target.
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const scrollToHash = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      const timer = setTimeout(scrollToHash, 150);
      return () => clearTimeout(timer);
    }
  }, []);

  // The header's "How It Works" nav link scrolls to the #how section - it
  // should also expand it, since it would otherwise land on a collapsed row.
  useEffect(() => {
    const handleOpenSection = (e) => setOpenSection(e.detail);
    window.addEventListener('petechtor:open-section', handleOpenSection);
    return () => window.removeEventListener('petechtor:open-section', handleOpenSection);
  }, []);

  const handleWaitlistSubmit = async (formData) => {
    const API_URL = getApiUrl();
    const referralCode = new URLSearchParams(window.location.search).get('ref') || '';
    const savedSource = getSavedSource();

    const response = await fetch(`${API_URL}/api/waitlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        referralCode,
        source: savedSource?.source || 'direct',
        campaign: savedSource?.campaign || '',
        firstSeen: savedSource?.firstSeen || '',
      }),
    });
    
    if (!response.ok) {
      let message = 'Failed to join waitlist. Please try again.';
      try {
        const body = await response.json();
        if (body?.detail) message = body.detail;
      } catch (e) {}
      throw new Error(message);
    }

    // Lets the Rainbow Room feedback section unlock immediately for this email
    // if/when it's approved, instead of waiting on the 3-minute tour lock.
    localStorage.setItem('petechtor_waitlist_email', formData.email.trim().toLowerCase());

    return await response.json();
  };

  const handleRainbowRoomFeedbackSubmit = async (formData) => {
    const API_URL = getApiUrl();

    const response = await fetch(`${API_URL}/api/rainbow-room-feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error('Failed to send feedback. Please try again.');
    }

    return await response.json();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <Helmet>
        <title>Petechtor | Guardians of the Petverse – Protect Every Sidekick</title>
        <meta name="description" content="Join the Guardians of the Petverse. A community-first project protecting pets through technology, memorials, and unconditional support." />
        <meta property="og:title" content="Petechtor | Guardians of the Petverse" />
        <meta property="og:description" content="Stand with us to protect every pet. Community-first, protection-focused." />
      </Helmet>
      
      <Header />
      <RainbowRoomPopup />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Hero 
          onSubmitRainbowRoomFeedback={handleRainbowRoomFeedbackSubmit}
        />
        
        <ThreePillars isOpen={openSection === 'pillars-section'} onToggle={() => toggleSection('pillars-section')} />
        
        <HowItWorks isOpen={openSection === 'how'} onToggle={() => toggleSection('how')} />
        
        <MemeWall isOpen={openSection === 'feed-section'} onToggle={() => toggleSection('feed-section')} />
        
        <TrustBlock isOpen={openSection === 'trust-section'} onToggle={() => toggleSection('trust-section')} />
        
        <PetversePreview isOpen={openSection === 'petverse-section'} onToggle={() => toggleSection('petverse-section')} />
        
        <JoinSection onSubmit={handleWaitlistSubmit} />
      </main>
      
      <Footer />
    </div>
  );
};

export default LandingPage;
